import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Game Data ────────────────────────────────────────────────────────────────
// yaw = degrees of camera rotation per mouse count per unit of in-game sensitivity
// cm/360 = 36000 / (sensitivity × yaw × DPI)
const GAMES = [
  {
    id: 'cs2',
    name: 'CS2 / CS:GO',
    shortName: 'CS2',
    yaw: 0.022,
    color: '#F59E0B',
    min: 0.1, max: 20, step: 0.01,
    defaultSens: 1.5,
    typical: '0.8 – 2.5',
    decimals: 2,
    approx: false,
    note: 'Industry reference standard',
    genre: 'FPS',
  },
  {
    id: 'valorant',
    name: 'Valorant',
    shortName: 'VALORANT',
    yaw: 0.07,
    color: '#EF4444',
    min: 0.01, max: 10, step: 0.001,
    defaultSens: 0.4,
    typical: '0.2 – 0.7',
    decimals: 3,
    approx: false,
    note: '1 VAL ≈ 3.18× CS2',
    genre: 'FPS',
  },
  {
    id: 'apex',
    name: 'Apex Legends',
    shortName: 'Apex',
    yaw: 0.022,
    color: '#FF6B35',
    min: 0.1, max: 10, step: 0.1,
    defaultSens: 1.5,
    typical: '1.0 – 3.5',
    decimals: 2,
    approx: false,
    note: '1:1 with CS2 at 110° FOV',
    genre: 'BR / FPS',
  },
  {
    id: 'overwatch2',
    name: 'Overwatch 2',
    shortName: 'OW2',
    yaw: 0.0066,
    color: '#F97316',
    min: 0.5, max: 100, step: 0.5,
    defaultSens: 6.0,
    typical: '4 – 15',
    decimals: 1,
    approx: false,
    note: '1 CS2 ≈ 3.33× OW2',
    genre: 'FPS',
  },
  {
    id: 'r6siege',
    name: 'Rainbow Six Siege',
    shortName: 'R6 Siege',
    yaw: 0.00223,
    color: '#10B981',
    min: 1, max: 100, step: 1,
    defaultSens: 13,
    typical: '10 – 30',
    decimals: 0,
    approx: true,
    note: 'Hipfire % sensitivity',
    genre: 'FPS',
    unit: '%',
  },
  {
    id: 'fortnite',
    name: 'Fortnite',
    shortName: 'Fortnite',
    yaw: 0.005588,
    color: '#A855F7',
    min: 0.1, max: 50, step: 0.1,
    defaultSens: 5.9,
    typical: '3 – 12',
    decimals: 1,
    approx: true,
    note: 'X-Axis % sensitivity',
    genre: 'BR',
    unit: '%',
  },
  {
    id: 'warzone',
    name: 'CoD: Warzone',
    shortName: 'Warzone',
    yaw: 0.0066,
    color: '#6B7280',
    min: 1, max: 20, step: 0.5,
    defaultSens: 6.0,
    typical: '4 – 10',
    decimals: 1,
    approx: true,
    note: 'Hipfire sensitivity',
    genre: 'BR / FPS',
  },
  {
    id: 'quake',
    name: 'Quake Champions',
    shortName: 'Quake',
    yaw: 0.022,
    color: '#00D4FF',
    min: 0.1, max: 20, step: 0.01,
    defaultSens: 1.5,
    typical: '0.5 – 3.0',
    decimals: 2,
    approx: false,
    note: 'Same scale as CS2',
    genre: 'FPS',
  },
];

const COMMON_DPIS = [400, 800, 1600, 3200];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function calcCm360(sens, dpi, yaw) {
  if (!sens || !dpi || !yaw) return 0;
  return 36000 / (sens * yaw * dpi);
}

function sensFromCm360(cm360, dpi, yaw) {
  if (!cm360 || !dpi || !yaw) return 0;
  return 36000 / (cm360 * dpi * yaw);
}

function fmt(val, decimals) {
  if (!isFinite(val)) return '—';
  return parseFloat(val.toFixed(decimals)).toString();
}

function edpiZone(edpi) {
  if (edpi < 400)  return { label: 'Very Low', color: '#EF4444' };
  if (edpi < 800)  return { label: 'Low', color: '#F59E0B' };
  if (edpi <= 2000) return { label: 'Competitive', color: '#10B981' };
  if (edpi <= 4000) return { label: 'High', color: '#F59E0B' };
  return { label: 'Very High', color: '#EF4444' };
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function SensitivityConverter() {
  React.useEffect(() => {
    document.title = 'Sensitivity Converter — CS2, VALORANT, Apex & more | NeuroRank';
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []);

  const [dpi, setDpi]                   = useState(800);
  const [customDpi, setCustomDpi]       = useState('');
  const [sourceId, setSourceId]         = useState('cs2');
  const [sourceSens, setSourceSens]     = useState(1.5);
  const [copied, setCopied]             = useState(null);

  const activeDpi = useMemo(() => {
    if (customDpi !== '') {
      const p = parseInt(customDpi, 10);
      return p > 0 ? p : dpi;
    }
    return dpi;
  }, [dpi, customDpi]);

  const sourceGame = useMemo(() => GAMES.find(g => g.id === sourceId), [sourceId]);

  const cm360 = useMemo(() => calcCm360(sourceSens, activeDpi, sourceGame?.yaw), [sourceSens, activeDpi, sourceGame]);

  const edpi = useMemo(() => activeDpi * sourceSens, [activeDpi, sourceSens]);

  const zone = useMemo(() => edpiZone(edpi), [edpi]);

  const conversions = useMemo(() => GAMES.map(game => {
    if (game.id === sourceId) return { ...game, sens: sourceSens, isSource: true };
    const raw = sensFromCm360(cm360, activeDpi, game.yaw);
    const clamped = Math.min(Math.max(raw, game.min), game.max);
    const outOfRange = raw < game.min || raw > game.max;
    return { ...game, sens: clamped, raw, outOfRange, isSource: false };
  }), [cm360, activeDpi, sourceId, sourceSens]);

  const handleSourceChange = useCallback((id) => {
    const game = GAMES.find(g => g.id === id);
    if (!game) return;
    setSourceId(id);
    // Keep cm/360 the same by recalculating sensitivity for new source game
    if (cm360 > 0) {
      const newSens = sensFromCm360(cm360, activeDpi, game.yaw);
      const clamped = Math.min(Math.max(newSens, game.min), game.max);
      setSourceSens(parseFloat(clamped.toFixed(game.decimals + 1)));
    } else {
      setSourceSens(game.defaultSens);
    }
  }, [cm360, activeDpi]);

  const handleDpiPreset = useCallback((val) => {
    setDpi(val);
    setCustomDpi('');
  }, []);

  const copySens = useCallback((id, val) => {
    navigator.clipboard.writeText(String(val)).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    });
  }, []);

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '40px 24px 80px' }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ marginBottom: 40 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, #00d4ff22, #00d4ff11)',
            border: '1px solid #00d4ff33',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22,
          }}>🖱️</div>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>
              Sensitivity Converter
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
              Universal cm/360 converter — 8 games, DPI-aware, competitive accuracy
            </p>
          </div>
        </div>
      </motion.div>

      {/* ── DPI Selector ──────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.08 }}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: '24px 28px',
          marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 14 }}>
          Mouse DPI
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {COMMON_DPIS.map(val => (
            <button
              key={val}
              onClick={() => handleDpiPreset(val)}
              style={{
                padding: '9px 20px',
                borderRadius: 10,
                border: `1px solid ${customDpi === '' && dpi === val ? 'var(--accent)' : 'var(--border)'}`,
                background: customDpi === '' && dpi === val ? 'var(--accent)15' : 'transparent',
                color: customDpi === '' && dpi === val ? 'var(--accent)' : 'var(--text-muted)',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {val.toLocaleString()}
            </button>
          ))}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Custom:</span>
            <input
              type="number"
              min="100"
              max="32000"
              value={customDpi}
              onChange={e => setCustomDpi(e.target.value)}
              placeholder="e.g. 1200"
              style={{
                width: 100,
                padding: '9px 12px',
                borderRadius: 10,
                border: `1px solid ${customDpi ? 'var(--accent)' : 'var(--border)'}`,
                background: customDpi ? 'var(--accent)15' : 'var(--bg)',
                color: customDpi ? 'var(--accent)' : 'var(--text-primary)',
                fontSize: 14, fontWeight: 700,
                outline: 'none',
                fontFamily: 'var(--font-mono)',
              }}
            />
          </div>

          <div style={{
            marginLeft: 'auto',
            padding: '8px 16px', borderRadius: 10,
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            fontSize: 13, fontWeight: 700,
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-mono)',
          }}>
            Active DPI: <span style={{ color: 'var(--accent)' }}>{activeDpi.toLocaleString()}</span>
          </div>
        </div>
      </motion.div>

      {/* ── Game + Sensitivity Input ───────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.14 }}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: '24px 28px',
          marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 16 }}>
          Your Game
        </div>

        {/* Game Selector */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 28 }}>
          {GAMES.map(game => (
            <button
              key={game.id}
              onClick={() => handleSourceChange(game.id)}
              style={{
                padding: '8px 16px',
                borderRadius: 10,
                border: `1px solid ${sourceId === game.id ? game.color + '88' : 'var(--border)'}`,
                background: sourceId === game.id ? game.color + '18' : 'transparent',
                color: sourceId === game.id ? game.color : 'var(--text-muted)',
                fontWeight: 700, fontSize: 12,
                cursor: 'pointer', letterSpacing: '0.04em',
                transition: 'all 0.15s',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
              }}
            >
              <span>{game.shortName}</span>
              <span style={{ fontSize: 9, fontWeight: 500, opacity: 0.7, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{game.genre}</span>
            </button>
          ))}
        </div>

        {/* Sensitivity Input */}
        <AnimatePresence mode="wait">
          <motion.div
            key={sourceId}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: sourceGame?.color }}>{sourceGame?.name}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.05em' }}>sensitivity</span>
              {sourceGame?.approx && (
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: 1.5, padding: '2px 7px',
                  borderRadius: 20, border: '1px solid #F59E0B55', color: '#F59E0B',
                  textTransform: 'uppercase',
                }}>Approx.</span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <input
                type="range"
                min={sourceGame?.min}
                max={sourceGame?.max}
                step={sourceGame?.step}
                value={sourceSens}
                onChange={e => setSourceSens(parseFloat(e.target.value))}
                style={{
                  flex: '1 1 200px', minWidth: 200,
                  accentColor: sourceGame?.color,
                  height: 6, cursor: 'pointer',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="number"
                  min={sourceGame?.min}
                  max={sourceGame?.max}
                  step={sourceGame?.step}
                  value={sourceSens}
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v)) setSourceSens(Math.min(Math.max(v, sourceGame.min), sourceGame.max));
                  }}
                  style={{
                    width: 90, padding: '10px 12px', borderRadius: 10,
                    border: `1px solid ${sourceGame?.color}55`,
                    background: sourceGame?.color + '10',
                    color: sourceGame?.color,
                    fontSize: 18, fontWeight: 900, textAlign: 'center',
                    outline: 'none', fontFamily: 'var(--font-mono)',
                  }}
                />
                {sourceGame?.unit && (
                  <span style={{ fontSize: 14, color: sourceGame?.color, fontWeight: 700 }}>{sourceGame.unit}</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Typical competitive: <span style={{ color: sourceGame?.color, fontWeight: 700 }}>{sourceGame?.typical}{sourceGame?.unit || ''}</span>
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {sourceGame?.note}
              </span>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* ── Stats Bar: cm/360 + eDPI ───────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}
      >
        {/* cm/360 */}
        <div style={{
          background: 'linear-gradient(135deg, #00d4ff15, #00d4ff08)',
          border: '1px solid #00d4ff33',
          borderRadius: 16, padding: '22px 24px',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>cm / 360°</span>
          <span style={{ fontSize: 32, fontWeight: 900, color: 'var(--accent)', fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
            {isFinite(cm360) ? cm360.toFixed(1) : '—'}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>centimetres per full rotation</span>
        </div>

        {/* eDPI */}
        <div style={{
          background: `${zone.color}12`,
          border: `1px solid ${zone.color}33`,
          borderRadius: 16, padding: '22px 24px',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>eDPI</span>
          <span style={{ fontSize: 32, fontWeight: 900, color: zone.color, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
            {Math.round(edpi).toLocaleString()}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1.5, padding: '2px 7px',
              borderRadius: 20, border: `1px solid ${zone.color}55`, color: zone.color, textTransform: 'uppercase',
            }}>{zone.label}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>DPI × sens</span>
          </div>
        </div>

        {/* Guide */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 16, padding: '20px 24px',
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>eDPI Zones</span>
          {[
            { range: '< 400',         label: 'Very Low',   color: '#EF4444' },
            { range: '400 – 799',     label: 'Low',        color: '#F59E0B' },
            { range: '800 – 2000',    label: 'Competitive',color: '#10B981' },
            { range: '2001 – 4000',   label: 'High',       color: '#F59E0B' },
            { range: '> 4000',        label: 'Very High',  color: '#EF4444' },
          ].map(z => (
            <div key={z.range} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{z.range}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: z.color }}>{z.label}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Conversion Results ─────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.26 }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 16 }}>
          Equivalent Sensitivities
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
          {conversions.map((game, i) => (
            <motion.div
              key={game.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              style={{
                background: game.isSource ? game.color + '18' : 'var(--surface)',
                border: `1px solid ${game.isSource ? game.color + '55' : 'var(--border)'}`,
                borderRadius: 14,
                padding: '18px 20px',
                position: 'relative',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onClick={() => copySens(game.id, fmt(game.sens, game.decimals))}
              onMouseEnter={e => {
                if (!game.isSource) {
                  e.currentTarget.style.borderColor = game.color + '55';
                  e.currentTarget.style.background = game.color + '10';
                }
              }}
              onMouseLeave={e => {
                if (!game.isSource) {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.background = 'var(--surface)';
                }
              }}
            >
              {/* Source badge */}
              {game.isSource && (
                <div style={{
                  position: 'absolute', top: 10, right: 10,
                  fontSize: 8, fontWeight: 700, letterSpacing: 1.5,
                  padding: '2px 7px', borderRadius: 20,
                  border: `1px solid ${game.color}55`, color: game.color,
                  textTransform: 'uppercase',
                }}>Source</div>
              )}

              {/* Approx badge */}
              {game.approx && !game.isSource && (
                <div style={{
                  position: 'absolute', top: 10, right: 10,
                  fontSize: 8, fontWeight: 700, letterSpacing: 1.5,
                  padding: '2px 7px', borderRadius: 20,
                  border: '1px solid #F59E0B44', color: '#F59E0B',
                  textTransform: 'uppercase',
                }}>~</div>
              )}

              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: '0.04em' }}>
                {game.name}
              </div>

              <div style={{
                fontSize: game.isSource ? 28 : 26,
                fontWeight: 900,
                color: game.color,
                fontFamily: 'var(--font-mono)',
                lineHeight: 1,
                marginBottom: 6,
              }}>
                {fmt(game.sens, game.decimals)}{game.unit || ''}
              </div>

              {/* Out of range warning */}
              {game.outOfRange && (
                <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 600, marginBottom: 4 }}>
                  ⚠ Outside normal range
                </div>
              )}

              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>
                Typical: <span style={{ color: game.color + 'CC', fontWeight: 600 }}>{game.typical}{game.unit || ''}</span>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{game.note}</div>

              {/* Copy feedback */}
              <AnimatePresence>
                {copied === game.id && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.85 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      position: 'absolute', inset: 0, borderRadius: 14,
                      background: game.color + '22',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 800, color: game.color,
                      letterSpacing: '0.06em',
                    }}
                  >
                    ✓ Copied
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* ── Reference Table ────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: '24px 28px',
          marginTop: 28,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 18 }}>
          Pro Player Reference — Sensitivities at 400 DPI
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {['Player', 'Game', 'DPI', 'In-Game Sens', 'eDPI', 'cm / 360'].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
                    color: 'var(--accent)', textTransform: 'uppercase',
                    borderBottom: '1px solid var(--border)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { player: 's1mple',      game: 'CS2',      dpi: 400, sens: 3.09,   gameId: 'cs2'       },
                { player: 'ZywOo',       game: 'CS2',      dpi: 400, sens: 2.0,    gameId: 'cs2'       },
                { player: 'NiKo',        game: 'CS2',      dpi: 400, sens: 1.2,    gameId: 'cs2'       },
                { player: 'TenZ',        game: 'Valorant', dpi: 800, sens: 0.408,  gameId: 'valorant'  },
                { player: 'ScreaM',      game: 'Valorant', dpi: 400, sens: 0.78,   gameId: 'valorant'  },
                { player: 'Shroud',      game: 'Apex',     dpi: 400, sens: 1.9,    gameId: 'apex'      },
                { player: 'Dropped',     game: 'Apex',     dpi: 1600, sens: 0.8,   gameId: 'apex'      },
                { player: 'Seagull',     game: 'OW2',      dpi: 800, sens: 3.0,    gameId: 'overwatch2'},
              ].map((row, i) => {
                const g = GAMES.find(gm => gm.id === row.gameId);
                const rowCm360 = g ? calcCm360(row.sens, row.dpi, g.yaw) : 0;
                return (
                  <tr key={row.player} style={{ background: i % 2 === 0 ? 'transparent' : '#ffffff05' }}>
                    <td style={{ padding: '11px 14px', fontWeight: 700, color: 'var(--text-primary)' }}>{row.player}</td>
                    <td style={{ padding: '11px 14px', color: g?.color, fontWeight: 600 }}>{row.game}</td>
                    <td style={{ padding: '11px 14px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{row.dpi}</td>
                    <td style={{ padding: '11px 14px', color: g?.color, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{row.sens}</td>
                    <td style={{ padding: '11px 14px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{(row.dpi * row.sens).toLocaleString()}</td>
                    <td style={{ padding: '11px 14px', color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                      {rowCm360.toFixed(1)} cm
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10, background: '#00d4ff08', border: '1px solid #00d4ff22' }}>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, lineHeight: 1.7 }}>
            <span style={{ color: 'var(--accent)', fontWeight: 700 }}>How to use: </span>
            Set your DPI → select your game → enter your sensitivity. All conversions update in real-time.
            Click any result card to copy the value. cm/360 is the universal metric — it tells you
            how many centimetres of mouse movement equals a full 360° turn, regardless of game.
            <span style={{ color: '#F59E0B' }}> ~ marks indicate approximate conversions</span> for games
            with non-standard or complex sensitivity scaling.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
